// 2026-10-09 bundle-budget: la escala vive en la hoja `@/lib/plan-rank` (sin
// dependencias). Antes se importaba de `@/lib/api-utils`, que depende de
// `@/lib/api-i18n` (≈114 KB de copy de error en 14 locales, solo servidor):
// como `Pricing.tsx` importa este módulo en cliente, toda esa tabla viajaba en
// el first-load de `/`. Ver plan-rank.ts.
import { planRank as apiPlanRank } from "@/lib/plan-rank";

/**
 * Catalogo de cobro para Wompi. Wompi opera en Colombia y unicamente
 * acepta pagos en COP, asi que mantenemos:
 *  - `priceUsd`: precio actual de mercado en USD (el que se cobra).
 *  - `originalPriceUsd`: precio de lista antes del descuento (para mostrar tachado).
 *  - `amountInCents` + `currency`: lo que enviamos a Wompi (COP + IVA 19%).
 *  - `amountBaseCents`: monto base en COP SIN IVA (para mostrar el desglose).
 *  - `copPerUsd`: tasa USD → COP usada al construir estos montos.
 *
 * TIPO DE CAMBIO DIARIO: por defecto los montos se construyen con la tasa de
 * mercado diaria de `src/lib/fx.ts` (open.er-api.com actualiza a diario, no
 * tick-by-tick; caché de 15 min — ver CheckoutContent "Tasa de mercado"). El
 * pin manual `COP_PER_USD` / `NEXT_PUBLIC_COP_PER_USD` sigue existiendo como
 * override determinista (y como fallback de red). El monto cobrado se
 * compromete al momento del cobro y el webhook verifica contra ese monto
 * exacto (ver `getBillingPlan(id, period, copPerUsd)` y la ruta del webhook).
 *
 * 2026-09-21 FASE 2 FIXER audit-payments #2: este pin import-time es
 * display/fallback determinista — la verificación del webhook NUNCA lo usa
 * (exige `pending_*_amount_cents` comprometido; sin él, fail closed sin
 * conceder). No reintroducir fallback de tasa en el money path sin ADR.
 *
 * Tarifas Wompi (Plan Avanzado, 2026): 2.65% + $700 COP + IVA sobre la comisión.
 * Esa comisión la absorbe el comercio; se muestra en el desglose para transparencia.
 *
 * SIN PRORRATEO (lote Fase 5, política documentada): no hay créditos
 * parciales. Un upgrade (ej. Pro→Max) es una compra nueva a precio completo
 * con efecto inmediato (aplica el webhook); una cancelación/baja rige al fin
 * del ciclo pagado (`subscription_cancel_at`, ver cancel/route.ts) sin
 * reembolso parcial. El checkout muestra el total exacto antes de pagar.
 */
// 2026-08-23 scale Wompi — COP only, IVA 19%, live FX 15min cache, 2.65%+700
// 2026-09-30 RUN10 F2-04a (P1) — pin fail-closed. El pin anterior era
// `Number.isFinite(Number(raw))`, que en JS acepta `""` y `" "` porque
// `Number("") === 0` y `Number(" ") === 0` (finito ⇒ true). Una env vacía
// fijaba la tasa del MONEY PATH en 0: `toCopCents(2, 0) → 0` ⇒ los planes
// cacheados (monthly+annual) con `amountInCents = 0`, y `getBillingPlan`
// (`safeRate === COP_PER_USD` fast path) devolvía ese plan de $0 — el guard
// `isValidCopRate` de la línea siguiente quedaba BYPASSEADO por el pin
// import-time. Ahora la env debe ser un número POSITIVO FINITO; vacío,
// `"0"`, `" "`, NaN o negativo → fallback determinista. Es exactamente la
// semántica de `fx.ts envPin()` (`if (!raw) return null; value > 0`), que ya
// era fail-closed — dos lectores de la misma variable con reglas distintas es
// la divergencia que se corrige aquí. `isValidCopRate` se reutiliza (hoisted)
// como predicado único: el guard queda en el pin, no solo en los consumidores.
// research 2026: JS `Number()` coercion table (MDN Number 2026-09-30) +
// fx.ts:97-102 (`envPin`) como precedente interno.
const DEFAULT_COP_PER_USD = 4_000;

function readCopRateEnv(raw: string | undefined): number | null {
  // `!raw` (y no `Number.isFinite(Number(raw))`) cubre undefined/""/" " —
  // los tres colapsaban a 0. Mismo criterio que fx.ts envPin().
  if (!raw) return null;
  const value = Number(raw);
  return isValidCopRate(value) ? value : null;
}

export const COP_PER_USD =
  readCopRateEnv(process.env.COP_PER_USD) ??
  readCopRateEnv(process.env.NEXT_PUBLIC_COP_PER_USD) ??
  DEFAULT_COP_PER_USD;

/** IVA colombiano sobre el precio del servicio digital: 19% */
export const IVA_RATE = 0.19;

/** Comisión Wompi (Plan Avanzado): 2.65% por transacción */
const WOMPI_FEE_PERCENT = 0.0265;
/** Comisión Wompi fija por transacción: $700 COP */
const WOMPI_FEE_FIXED_COP = 700;

/**
 * 2026-09-25 FASE 4 MEJORA planes-pagos M1 (money-path SSOT): el guard de tasa
 * COP válida (`Number.isFinite && >0`) estaba triplicado — `getBillingPlan`
 * (safeRate), `CheckoutContent` (rate) y `web-checkout` (displayedRate). Una
 * sola definición con la MISMA semántica fail-closed (inválida → fallback
 * `COP_PER_USD` / 409, nunca NaN al money path). La plausibilidad de mercado
 * (500–20_000) vive en `fx.ts` `isPlausible` para el fetch vivo; aquí solo se
 * rechaza lo no-finito/no-positivo. Sin precios, sin copy, sin visual.
 */
export function isValidCopRate(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export type BillingPlanId = "go" | "pro" | "max" | "maxplus";
export type PlanId = BillingPlanId;
export type BillingPlanPeriod = "monthly" | "annual";

// RESUELTO 2026-09-12 [G-SEV-bajo] parsers compartidos alias↔canónico reconcile.
// Ayer mismo divergieron en maxplus (el alias olvidó el 5º plan) — una sola
// definición impide la re-divergencia |auditoría fase G
export function asPaidPlanId(value: unknown): BillingPlanId | null {
  // FASE 4 oferta-8 D3 (wire): id runtime-tolerante ("Go" → go).
  return normalizePaidPlanId(value);
}

export function asBillingPeriod(value: unknown): BillingPlanPeriod {
  // FASE 6 oferta-8 P2 (wire): trim+lowercase (paridad dashboard).
  return normalizeBillingPeriodValue(value);
}

export interface BillingPlan {
  id: BillingPlanId;
  /** Precio actual cobrado en USD (el nuevo precio con descuento) */
  priceUsd: number;
  /** Precio original antes del descuento, para mostrar tachado */
  originalPriceUsd: number;
  /** Porcentaje de descuento respecto al precio original */
  discountPercent: number;
  /** Monto base en COP sin IVA (priceUsd × copPerUsd), en centavos */
  amountBaseCents: number;
  /** IVA en COP (19% sobre la base), en centavos */
  ivaAmountCents: number;
  /** Monto total en COP con IVA incluido — este es el que se envía a Wompi */
  amountInCents: number;
  /** Tasa USD → COP usada al construir este plan (p.ej. 3.137,90) */
  copPerUsd: number;
  currency: "COP";
  period: BillingPlanPeriod;
  savingsPercent?: number;
}

function toCopBase(usd: number, copPerUsd: number): number {
  // 2026-08-29 fix: Math.round(usd * copPerUsd * 100) — evita error ≤100 COP con tasas fraccionales (Task 37)
  // 2026-09-01 autopilot Task 20 — verificado fail-closed, *100 dentro de Math.round (no Math.round(usd*cop)*100)
  // 2026-09-25 FASE 4 MEJORA planes-pagos M1 (SSOT aritmética): delega en
  // toCopCents — una sola fórmula Math.round(usd*cop*100), cero divergencia.
  return toCopCents(usd, copPerUsd);
}

export function toCopCents(usd: number, copPerUsd: number): number {
  // 2026-09-01 autopilot Task 20 — Math.round(usd*copPerUsd*100) con COP_PER_USD env o 4000; evita 10 COP off en 3137.90
  // 2026-09-26 FASE 4 MEJORA planes-pagos (fail-closed en la fuente): la tasa
  // ya la blinda getBillingPlan (safeRate) y el display lo blinda formatCop,
  // pero esta aritmética SSOT es exportada — una entrada no-finita/no-positiva
  // (NaN/Infinity/≤0) propagaba NaN/negativos al BillingPlan en vez de fallar
  // a $0. Puro, sin precios, sin copy.
  if (!Number.isFinite(usd) || !Number.isFinite(copPerUsd)) return 0;
  if (usd <= 0 || copPerUsd <= 0) return 0;
  return Math.round(usd * copPerUsd * 100);
}

export function applyIva(baseCents: number | null | undefined): number {
  // IVA del 19% sobre el valor base (fail-closed ante no-finito o <= 0)
  if (
    typeof baseCents !== "number" ||
    !Number.isFinite(baseCents) ||
    baseCents <= 0
  ) {
    return 0;
  }
  return Math.round(baseCents * IVA_RATE);
}

function buildPlan(
  id: BillingPlanId,
  priceUsd: number,
  originalPriceUsd: number,
  period: BillingPlanPeriod,
  copPerUsd: number,
  savingsPercent?: number,
): BillingPlan {
  // FASE 4 oferta-8 D1 (wire abajo): % descuento saneado finito.
  const discountPercent = normalizeDiscountPercent(priceUsd, originalPriceUsd);
  const amountBaseCents = toCopBase(priceUsd, copPerUsd);
  const ivaAmountCents = applyIva(amountBaseCents);
  // 2026-09-24 FASE 4 MEJORA planes-pagos (money-path compartido): los planes
  // cacheados (BILLING_PLANS_MONTHLY/ANNUAL) se devuelven por referencia — una
  // mutación accidental en cualquier consumidor envenenaría checkout + webhook
  // + Pricing a la vez. Congelado shallow (todos los campos son primitivos):
  // falla ruidoso en dev en vez de corromper montos en silencio.
  return Object.freeze({
    id,
    priceUsd,
    originalPriceUsd,
    discountPercent,
    amountBaseCents,
    ivaAmountCents,
    amountInCents: amountBaseCents + ivaAmountCents,
    copPerUsd,
    currency: "COP",
    period,
    savingsPercent,
  });
}

/** Single source of truth for current discounted USD prices */
// 2026-10-16 precios fijos (research 2026: banda estándar $20/mes, banda budget ~$5-8;
// Go $4 / Pro $8 / Max $14 / Max+ $24 queda debajo de ambas y el fijo Wompi $700 pasa de
// morder ~20% (Go $1 anterior) a proporciones mínimas).
export const GO_PRICE_USD = 4;
export const PRO_PRICE_USD = 8;
export const MAX_PRICE_USD = 14;
// 2026-10-16 Max+ $24 — 2x cuotas diarias de Max a $24/mes. Cash peor caso
// ~$3.43/mes (imgs overage) vs ~$23 netos tras Wompi.
export const MAXPLUS_PRICE_USD = 24;

// 2026-10-16 precios fijos sin tachados artificiales (original = price, 0% off).
/** Original (pre-discount) USD list prices — matches priceUsd when no discount */
export const GO_ORIGINAL_PRICE_USD = 4;
export const PRO_ORIGINAL_PRICE_USD = 8;
export const MAX_ORIGINAL_PRICE_USD = 14;
// Max+ ancla $24 (original = price).
export const MAXPLUS_ORIGINAL_PRICE_USD = 24;

/**
 * Annual pricing is derived from monthly: pay for N months, get 12.
 * Keeps annual list prices locked to the monthly SSOT (no hard-coded duals).
 */
export const ANNUAL_BILLED_MONTHS = 10;
export const ANNUAL_SAVINGS_PERCENT = 17; // 2026-08-23: 17% = 10/12 months, verified honest savings copy

/**
 * Porcentaje de ahorro en cobro anual vs mensual (10/12 meses pagados = 17% de ahorro honesto).
 * Sourced from ANNUAL_SAVINGS_PERCENT SSOT (10/12 = 83.33% cobrado, 16.67% ~ 17% ahorro).
 * Planes pagos ('go', 'pro', 'max', 'maxplus') devuelven 17; 'free' o desconocido devuelven 0.
 */
export function getAnnualSavingsPercent(plan?: PlanId | string | null): number {
  if (plan === undefined) return ANNUAL_SAVINGS_PERCENT;
  if (typeof plan !== "string" || plan.trim().toLowerCase() === "free")
    return 0;
  const id = normalizePaidPlanId(plan);
  return id !== null ? ANNUAL_SAVINGS_PERCENT : 0;
}

export function annualPriceFromMonthly(monthlyUsd: number): number {
  // FASE 4 oferta-8 D2 (wire): derivado anual saneado (NaN/Infinity/≤0 → 0,
  // nunca NaN al COP ni al toggle anual).
  if (!Number.isFinite(monthlyUsd) || monthlyUsd <= 0) return 0;
  const annual = monthlyUsd * ANNUAL_BILLED_MONTHS;
  return Number.isFinite(annual) && annual > 0 ? annual : 0;
}

// 2026-09-21 FASE 4 MEJORA planes-pagos P1 (research 2026 pricing pages:
// mostrar el ahorro anual en USD además del % — "save $X/yr" convierte mejor
// que solo "Save 17%"): derivados del SSOT mensual, sin literales duales.
export function annualEquivalentPerMonthUsd(monthlyUsd: number): number {
  // FASE 4 oferta-8 D2 (wire): equivalente mensual saneado (mensual
  // inválido o anual derivado inválido → 0, nunca NaN al "billed annually").
  if (!Number.isFinite(monthlyUsd) || monthlyUsd <= 0) return 0;
  const equiv = annualPriceFromMonthly(monthlyUsd) / 12;
  return Number.isFinite(equiv) && equiv > 0 ? equiv : 0;
}

export function annualSavedUsdVsMonthly(monthlyUsd: number): number {
  // FASE 6 oferta-8 P1 (wire): guard finito (NaN/Infinity/≤0 → 0, nunca ahorro
  // negativo al display).
  if (!Number.isFinite(monthlyUsd) || monthlyUsd <= 0) return 0;
  const saved = monthlyUsd * 12 - annualPriceFromMonthly(monthlyUsd);
  return Number.isFinite(saved) && saved > 0 ? saved : 0;
}

// ---------------------------------------------------------------------------
// FASE 6 competencia oferta-8 PLANES (2026-09-28, SEED=2026-09-28-bae4824d).
// Dos mecánicas NUEVAS, backend puro, 0 controles visibles, 0 keys i18n.
// Research 2026:
// - SaaSpare 2026-08-07 pricing-changes (daily diffs; la promesa "ahorro = 2
//   meses" debe ser exacta en centavos) + SaasTrack 2026 (quota moves no
//   anunciados rompen confianza) — ver websearch 2026 en reporte.
// - Chargebee 2026 usage-based-pricing (transparencia del cargo estimado).
// ---------------------------------------------------------------------------

/**
 * [P1 billing-annual-saved-guard-finite] Ahorro anual USD saneado (ver wire
 * arriba). V2 medible vs excluidos: annualSavedCopCents ya era fail-closed,
 * pero annualSavedUsdVsMonthly propagaba NaN con monthly NaN (el toggle anual
 * pintaba "save $NaN/yr"). Puro, sin copy.
 * Fingerprint `billing-annual-saved-guard-finite` (guard real en
 * `annualSavedUsdVsMonthly`; test: billing-plans-fase6-oferta8.test.ts).
 * 2026-09-28 FASE 2 RUN6 Q1: la const `_BILLING_ANNUAL_SAVED_GUARD_FINGERPRINT`
 * se borró (0 referencias en src/ y scripts/; el prefijo `_` solo callaba
 * ESLint) — el fingerprint sobrevive en este comentario y en el nombre del test.
 */

/**
 * [P2 billing-period-trim-lower-normalize] Normaliza el periodo de cobro:
 * trim+lowercase; "annual" (cualquier caja/espacios) → "annual", resto →
 * "monthly". V2 medible vs excluidos: asBillingPeriod hacía `value ===
 * "annual"` estricto ("Annual", " ANNUAL " caían a monthly en silencio);
 * resolveBillingPeriod (dashboard) ya plegaba pero el money-path no.
 * Puro, sin copy. Fingerprint `billing-period-trim-lower-normalize`.
 */
export function normalizeBillingPeriodValue(value: unknown): BillingPlanPeriod {
  if (typeof value !== "string") return "monthly";
  return value.trim().toLowerCase() === "annual" ? "annual" : "monthly";
}

// ---------------------------------------------------------------------------
// FASE 4 oferta-8 PLANES+PAGOS (2026-09-28, SEED=2026-09-28-bae4824d).
// Cinco mecánicas NUEVAS, backend puro, 0 controles visibles, 0 keys i18n.
// Research 2026 (websearch 2026-09-28):
// - vezadigital 2026 "Best SaaS Pricing Page Examples" (transparent savings:
//   show annual discount % + absolute savings clearly) + fungies.io
//   2026-05-27 "Annual billing toggle alone can increase…" (show the dollar
//   amount "Save $X/yr", per-month equivalent for annual plans).
// - docs.wompi.co Impuestos 2026 (IVA 19% informativo) + Wompi Plan Avanzado
//   2.65%+$700: el desglose COP nunca pinta NaN/Infinity al comprador.
// ---------------------------------------------------------------------------

/**
 * [D1 billing-discount-percent-guard-finite] % descuento saneado 0..100
 * finito (original ≤0/no-finito → 0). V2 medible vs excluidos: buildPlan
 * calculaba Math.round(((orig-price)/orig)*100) sin guard — un original
 * 0/NaN runtime (tasa corrupta, plan reconstruido) pintaba NaN%/Infinity%
 * en el tachado de Pricing/checkout. Precios SSOT intactos (Go 4/4→0%,
 * Pro 8/8→0%, Max 14/14→0%, Max+ 24/24→0%). Puro, sin copy.
 * Fingerprint `billing-discount-percent-guard-finite`.
 */
function normalizeDiscountPercent(
  priceUsd: unknown,
  originalPriceUsd: unknown,
): number {
  if (
    !Number.isFinite(priceUsd) ||
    !Number.isFinite(originalPriceUsd) ||
    (originalPriceUsd as number) <= 0
  )
    return 0;
  const price = priceUsd as number;
  const orig = originalPriceUsd as number;
  if (price < 0) return 0;
  const pct = Math.round(((orig - price) / orig) * 100);
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, pct));
}

/**
 * [D2 billing-annual-deriv-guard-finite] Derivados anuales saneados (ver
 * wires en annualPriceFromMonthly/annualEquivalentPerMonthUsd). V2 medible
 * vs excluidos: annualSavedUsdVsMonthly ya era fail-closed (FASE 6 P1),
 * pero los dos derivados base propagaban NaN (monthly NaN → anual NaN →
 * COP NaN en getBillingPlan vivo + toggle "save $NaN/yr"). Reales:
 * 4→40, 8→80, 14→140, 24→240 USD; equiv 40/12≈3.33. Puro, sin copy.
 * Fingerprint `billing-annual-deriv-guard-finite` (guard real en
 * `annualPriceFromMonthly` + `annualEquivalentPerMonthUsd`; test:
 * billing-plans-fase6-oferta8.test.ts).
 * 2026-09-28 FASE 2 RUN6 Q1: borrada la const
 * `_BILLING_ANNUAL_DERIV_GUARD_FINGERPRINT` (0 referencias) y añadido el test
 * que faltaba (antes solo el guard P1 tenía cobertura).
 */

/**
 * [D3 billing-plan-id-trim-lower-normalize] Normaliza el id de plan:
 * trim+lowercase; "go"/"pro"/"max"/"maxplus" (cualquier caja/espacios) →
 * canónico, resto → null. V2 medible vs excluidos: asPaidPlanId hacía
 * `value === "go"` estricto ("Go", " PRO " del webhook/query caían a null
 * → 400 inválido aunque el plan existe); el periodo ya plegaba (FASE 6
 * P2) pero el id no. Puro, sin copy.
 * Fingerprint `billing-plan-id-trim-lower-normalize`.
 */
function normalizePaidPlanId(value: unknown): BillingPlanId | null {
  if (typeof value !== "string") return null;
  const clean = value.trim().toLowerCase();
  return clean === "go" ||
    clean === "pro" ||
    clean === "max" ||
    clean === "maxplus"
    ? clean
    : null;
}

/**
 * [D5 wompi-taxes-null-safe-empty] Taxes Wompi null-safe (plan null o IVA
 * inválido → []). V2 medible vs excluidos: wompiTaxesFor leía
 * plan.ivaAmountCents sin guard — un plan null runtime (getBillingPlan
 * fallido propagado) lanzaba TypeError en el money path server-side en vez
 * de omitir el campo informativo. IVA 19% SSOT intacto. Puro, sin copy.
 * Fingerprint `wompi-taxes-null-safe-empty` (guard real en `wompiTaxesFor`;
 * test: billing-plans-fase6-oferta8.test.ts).
 * 2026-09-28 FASE 2 RUN6 Q1: borrada la const
 * `_WOMPI_TAXES_NULL_SAFE_FINGERPRINT` (0 referencias) y añadido el test que
 * faltaba — el guard se usaba en transactions/subscription/process-renewals
 * sin ninguna cobertura.
 */

// 2026-09-24 FASE 4 MEJORA planes-pagos (research 2026 Fairview 2026-05-29 +
// inbuild 2026-05-17: el ahorro anual convierte mejor en doble formato % +
// absoluto "$X/yr" — Pricing ya mostraba USD vía annualSavedUsdVsMonthly y COP
// vía cálculo inline mensual×12−anual; docs.wompi.co Impuestos 2026: el IVA es
// informativo dentro del total, así que el ahorro COP se calcula sobre
// amountInCents totales con la MISMA tasa — nunca mezclar tasas viva/catálogo).
// SSOT derivado sin literales: monthly/annual salen de getBillingPlan con la
// misma tasa; ≤0 (redondeo) → 0 para no renderizar ahorro negativo.
export function annualSavedCopCents(
  monthly: Pick<BillingPlan, "amountInCents"> | null,
  annual: Pick<BillingPlan, "amountInCents"> | null,
): number {
  if (!monthly || !annual) return 0;
  // FASE 4 MEJORA MASIVA v2 oferta-8 B2 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 vezadigital "Best SaaS Pricing Page Examples" + fungies.io
  // 2026-05-27 "show the dollar amount Save $X/yr": el ahorro COP debe ser
  // exacto en centavos). Antes: `monthly.amountInCents * 12` con coerción
  // implícita — un amount string runtime ("100000" desde metadata corrupta)
  // calculaba ahorro fantasma en vez de fail-closed a 0. Ahora: ambos montos
  // deben ser number finitos; si no, 0. Sanos intactos.
  // Fingerprint `billing-annual-cop-strict-number`.
  const m = monthly.amountInCents;
  const a = annual.amountInCents;
  if (!Number.isFinite(m) || !Number.isFinite(a)) return 0;
  const savings = (m as number) * 12 - (a as number);
  return Number.isFinite(savings) && savings > 0 ? savings : 0;
}

/**
 * 2026-09-26 FASE 6 COMPETENCIA oferta 7 M2 costo-diario honesto (research
 * 2026 userflow "SaaS Pricing Strategy" — "a transparent pricing page lets a
 * buyer estimate the bill … Show the plan price, the billing unit, the
 * limits" + Stigg 2026 "fair billing" verificado vía webfetch 2026-09-26):
 * equivalente diario en centavos COP del total con IVA (mensual ÷30,
 * anual ÷365). Derivado del SSOT `amountInCents` con la tasa ya comprometida
 * — nunca recalcula montos ni toca tasas. Monto inválido → 0 (nunca NaN ni
 * negativo al display). Puro, sin copy (cero keys i18n nuevas), sin visual.
 * Fingerprint `plan-daily-equivalent-cop`.
 */
export function getPlanDailyEquivalentCop(
  plan: Pick<BillingPlan, "amountInCents" | "period"> | null | undefined,
): number {
  if (!plan || !Number.isFinite(plan.amountInCents) || plan.amountInCents <= 0)
    return 0;
  const days = plan.period === "annual" ? 365 : 30;
  return Math.round(plan.amountInCents / days);
}

/**
 * 2026-09-23 FASE 4 MEJORA oferta 7 P1 (past_due sin salida): un usuario
 * `past_due` con ventana viva conserva su plan efectivo (gracia de cobro —
 * resolvePlanTier en plans.ts) pero `hasActivePaidPlan=false`, así que Pricing
 * marcaba su propia tarjeta como "Current" deshabilitado: sin forma de reparar
 * el pago desde la ruta de compra. Con ventana viva + mismo plan + past_due el
 * CTA debe ser checkout (reparar), no "Current". Sin ventana viva el plan
 * efectivo ya es free y el flujo normal de compra aplica (devuelve false).
 * Validación en servidor intacta: web-checkout permite past_due (status !=
 * active → currentPaidPlan null) y el webhook sana al conceder.
 */
export function isPlanRepairCheckout(
  authPlan: string | null | undefined,
  subscriptionStatus: string | null | undefined,
  paidPlan: BillingPlanId | null,
): boolean {
  // FASE 4 MEJORA MASIVA v2 oferta-8 B4 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 influenceflow.io "SaaS Pricing Page Best Practices 2026":
  // el CTA de cada tarjeta debe reflejar el estado real — un past_due que ve
  // "Current" deshabilitado no puede reparar). Antes: `authPlan === paidPlan`
  // estricto — "Pro" (query/metadata con mayúscula) vs "pro" canónico fallaba
  // y Pricing marcaba "Current" en vez de checkout de reparación. Ahora: el
  // plan de la suscripción se normaliza (trim+lowercase, paridad FASE 4 D3);
  // desconocido → null → false (fail-closed). Sanos intactos.
  // Fingerprint `billing-repair-plan-case-normalize`.
  return (
    paidPlan !== null &&
    normalizePaidPlanId(authPlan) === paidPlan &&
    subscriptionStatus === "past_due"
  );
}

const GO_ANNUAL_USD = annualPriceFromMonthly(GO_PRICE_USD);
const PRO_ANNUAL_USD = annualPriceFromMonthly(PRO_PRICE_USD);
const MAX_ANNUAL_USD = annualPriceFromMonthly(MAX_PRICE_USD);
const MAXPLUS_ANNUAL_USD = annualPriceFromMonthly(MAXPLUS_PRICE_USD);
const GO_ANNUAL_ORIG = annualPriceFromMonthly(GO_ORIGINAL_PRICE_USD);
const PRO_ANNUAL_ORIG = annualPriceFromMonthly(PRO_ORIGINAL_PRICE_USD);
const MAX_ANNUAL_ORIG = annualPriceFromMonthly(MAX_ORIGINAL_PRICE_USD);
const MAXPLUS_ANNUAL_ORIG = annualPriceFromMonthly(MAXPLUS_ORIGINAL_PRICE_USD);

const BILLING_PLANS_MONTHLY: Record<BillingPlanId, BillingPlan> = {
  go: buildPlan(
    "go",
    GO_PRICE_USD,
    GO_ORIGINAL_PRICE_USD,
    "monthly",
    COP_PER_USD,
  ),
  pro: buildPlan(
    "pro",
    PRO_PRICE_USD,
    PRO_ORIGINAL_PRICE_USD,
    "monthly",
    COP_PER_USD,
  ),
  max: buildPlan(
    "max",
    MAX_PRICE_USD,
    MAX_ORIGINAL_PRICE_USD,
    "monthly",
    COP_PER_USD,
  ),
  maxplus: buildPlan(
    "maxplus",
    MAXPLUS_PRICE_USD,
    MAXPLUS_ORIGINAL_PRICE_USD,
    "monthly",
    COP_PER_USD,
  ),
};

const BILLING_PLANS_ANNUAL: Record<BillingPlanId, BillingPlan> = {
  go: buildPlan(
    "go",
    GO_ANNUAL_USD,
    GO_ANNUAL_ORIG,
    "annual",
    COP_PER_USD,
    ANNUAL_SAVINGS_PERCENT,
  ),
  pro: buildPlan(
    "pro",
    PRO_ANNUAL_USD,
    PRO_ANNUAL_ORIG,
    "annual",
    COP_PER_USD,
    ANNUAL_SAVINGS_PERCENT,
  ),
  max: buildPlan(
    "max",
    MAX_ANNUAL_USD,
    MAX_ANNUAL_ORIG,
    "annual",
    COP_PER_USD,
    ANNUAL_SAVINGS_PERCENT,
  ),
  maxplus: buildPlan(
    "maxplus",
    MAXPLUS_ANNUAL_USD,
    MAXPLUS_ANNUAL_ORIG,
    "annual",
    COP_PER_USD,
    ANNUAL_SAVINGS_PERCENT,
  ),
};

export const BILLING_PLANS = BILLING_PLANS_MONTHLY;

// 2026-09-25 FASE 4 MEJORA planes-pagos M1 (money-path compartido, paridad con
// el freeze por plan en buildPlan): los mapas también se congelan — antes solo
// cada BillingPlan estaba frozen y el Record seguía mutable (reasignar una key
// envenenaría checkout + webhook + Pricing a la vez). Shallow basta (los
// valores ya son frozen con campos primitivos).
Object.freeze(BILLING_PLANS_MONTHLY);
Object.freeze(BILLING_PLANS_ANNUAL);

/**
 * Devuelve el plan de cobro para `id` y `period`.
 *
 * `copPerUsd` permite reconstruir los montos COP con la tasa del dólar
 * (mercado diaria). Por defecto usa `COP_PER_USD` (pin de entorno o 4000),
 * que es también el valor con el que se construyen los planes cacheados.
 *
 * El monto exacto de una compra se decide con la tasa vigente al momento del
 * cobro y se compromete en el metadata del usuario para que el webhook
 * verifique contra el monto cobrado, no contra una tasa recalculada.
 */
export function getBillingPlan(
  id: string,
  period: BillingPlanPeriod = "monthly",
  copPerUsd: number = COP_PER_USD,
): BillingPlan | null {
  // FASE 4 oferta-8 D3 (wire): id runtime-tolerante ("Go" → go, paridad periodo).
  const planId = normalizePaidPlanId(id);
  if (planId === null) return null;
  // FASE 6 oferta-8 P2 (wire): periodo runtime-tolerante ("Annual" → annual).
  const safePeriod = normalizeBillingPeriodValue(period);

  // 2026-09-30 RUN10 F2-04a: `COP_PER_USD` es válido POR CONSTRUCCIÓN desde el
  // fix del pin (readCopRateEnv, fail-closed) — este fallback cubre solo el
  // argumento explícito de quien llama. Invariante: ni el pin ni un argumento
  // inválido pueden devolver un plan con `amountInCents === 0`.
  const safeRate = isValidCopRate(copPerUsd) ? copPerUsd : COP_PER_USD;

  // Tasa por defecto: devolver los planes cacheados (fast path estándar).
  if (safeRate === COP_PER_USD) {
    return safePeriod === "annual"
      ? BILLING_PLANS_ANNUAL[planId]
      : BILLING_PLANS_MONTHLY[planId];
  }

  // Tasa en vivo: reconstruir el plan con la tasa actual del dólar.
  const priceUsd =
    planId === "go"
      ? GO_PRICE_USD
      : planId === "pro"
        ? PRO_PRICE_USD
        : planId === "max"
          ? MAX_PRICE_USD
          : MAXPLUS_PRICE_USD;
  const originalUsd =
    planId === "go"
      ? GO_ORIGINAL_PRICE_USD
      : planId === "pro"
        ? PRO_ORIGINAL_PRICE_USD
        : planId === "max"
          ? MAX_ORIGINAL_PRICE_USD
          : MAXPLUS_ORIGINAL_PRICE_USD;
  if (safePeriod === "annual") {
    // 2026-09-23 FASE 2 FIXES PAY-09 paridad COP anual redondeo (sin tocar
    // precios/quotas SSOT — solo redondeo): con tasa fraccionaria (p.ej.
    // 3.137,91) el doble redondeo Math.round(base)+Math.round(base*IVA)
    // aplicado sobre USD anual vs mensual×10 diverge unos centavos (ej. IVA
    // 2.981.015 vs 2.981.010 — sub-peso, invisible en $ formatCop, pero la
    // promesa "ahorro = 2 meses" debe ser exacta en centavos). El anual deriva
    // del mensual vivo ×ANNUAL_BILLED_MONTHS: ahorro exacto 2×mensual en
    // cualquier tasa; el IVA anual queda a ≤5 centavos de round(base*19%)
    // (mismo orden que el redondeo base). USD/origen/descuento intactos.
    const monthlyLive = buildPlan(
      planId,
      priceUsd,
      originalUsd,
      "monthly",
      safeRate,
    );
    const annualBaseCents = monthlyLive.amountBaseCents * ANNUAL_BILLED_MONTHS;
    const annualIvaCents = monthlyLive.ivaAmountCents * ANNUAL_BILLED_MONTHS;
    const annualShell = buildPlan(
      planId,
      annualPriceFromMonthly(priceUsd),
      annualPriceFromMonthly(originalUsd),
      "annual",
      safeRate,
      ANNUAL_SAVINGS_PERCENT,
    );
    return Object.freeze({
      ...annualShell,
      amountBaseCents: annualBaseCents,
      ivaAmountCents: annualIvaCents,
      amountInCents: annualBaseCents + annualIvaCents,
    });
  }
  return buildPlan(planId, priceUsd, originalUsd, "monthly", safeRate);
}

/**
 * Formatea centavos COP como moneda es-CO (p.ej. "$9.520").
 * SSOT para cualquier superficie que muestre montos COP (checkout, historial, admin, Discord).
 * Maneja entradas negativas o no finitas fijándolas a 0 (fail-closed, nunca NaN o negativos al display).
 */
export function formatCop(cents: number | null | undefined): string {
  // 2026-09-24 FASE 4 MEJORA planes-pagos (COP desglose fail-closed): este es
  // el SSOT de display para checkout/historial/admin/Discord — un monto
  // corrupto (NaN/Infinity/negativo) nunca debe renderizar "NaN"/"-$…" al
  // usuario; se fija a $0. El money path real sigue validado por firma +
  // committed amount en el webhook; esto es solo display, nunca concede.
  const safe =
    typeof cents === "number" && Number.isFinite(cents) && cents > 0
      ? cents
      : 0;
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(safe / 100);
}

/**
 * Formatea el monto base del plan (sin IVA) en COP.
 * p.ej. "$20.000" para el plan Pro ($5).
 * Acepta plan null/undefined o montos corruptos de forma segura.
 */
export function formatCopBase(
  plan: Pick<BillingPlan, "amountBaseCents"> | null | undefined,
): string {
  if (!plan || typeof plan !== "object") return formatCop(0);
  return formatCop(plan.amountBaseCents);
}

/**
 * Formatea el IVA del plan en COP.
 * p.ej. "$3.800" para el plan Pro ($5).
 * Acepta plan null/undefined o montos corruptos de forma segura.
 */
export function formatCopIva(
  plan: Pick<BillingPlan, "ivaAmountCents"> | null | undefined,
): string {
  if (!plan || typeof plan !== "object") return formatCop(0);
  return formatCop(plan.ivaAmountCents);
}

/**
 * Formatea el monto total del plan en COP (base + IVA).
 * p.ej. "$23.800" para el plan Pro ($5).
 * Este es el monto exacto que se cobra a Wompi.
 * Acepta plan null/undefined o montos corruptos de forma segura.
 */
export function formatCopAmount(
  plan: Pick<BillingPlan, "amountInCents"> | null | undefined,
): string {
  if (!plan || typeof plan !== "object") return formatCop(0);
  return formatCop(plan.amountInCents);
}

export interface IvaBreakdown {
  /** Monto base en COP sin IVA (en centavos) */
  baseCents: number;
  /** IVA del 19% en COP (en centavos) */
  ivaCents: number;
  /** Monto total en COP (base + IVA, en centavos) */
  totalCents: number;
  /** Tasa de IVA aplicada (0.19) */
  ivaRate: number;
  /** Monto base formateado en COP */
  formattedBase: string;
  /** IVA formateado en COP */
  formattedIva: string;
  /** Monto total formateado en COP */
  formattedTotal: string;
}

/**
 * Calcula y formatea el desglose de IVA del 19% a partir de un valor base en centavos COP.
 * Maneja entradas negativas o no finitas devolviendo ceros de forma segura.
 */
export function getIvaBreakdown(
  baseCents: number | null | undefined,
): IvaBreakdown {
  const safeBase =
    typeof baseCents === "number" && Number.isFinite(baseCents) && baseCents > 0
      ? Math.round(baseCents)
      : 0;
  const ivaCents = applyIva(safeBase);
  const totalCents = safeBase + ivaCents;
  return {
    baseCents: safeBase,
    ivaCents,
    totalCents,
    ivaRate: IVA_RATE,
    formattedBase: formatCop(safeBase),
    formattedIva: formatCop(ivaCents),
    formattedTotal: formatCop(totalCents),
  };
}

export const calculateIvaBreakdown = getIvaBreakdown;

/**
 * Calcula cuánto retiene Wompi de una transacción.
 * Wompi cobra: (monto × 2.65%) + $700 COP + IVA 19% sobre esa comisión.
 * Esto lo absorbe el comercio (no se cobra al cliente), pero se muestra para transparencia.
 */
export function calcWompiRetentionCop(plan: BillingPlan | null | undefined): {
  feeVariableCop: number;
  feeFixedCop: number;
  feeIvaCop: number;
  totalFeeCop: number;
  netCop: number;
} {
  // 2026-09-24 FASE 4 MEJORA planes-pagos M1 (research 2026 tinyfish
  // "2026 SaaS pricing page annual billing savings transparency best
  // practices": vezadigital 2026-03-06 + fungies.io 2026-05-27 — mostrar todos
  // los costos temprano; la retención es display de transparencia y
  // docs.wompi.co Impuestos 2026 fija 2.65% + $700 + IVA 19%): fail-closed a
  // nivel struct — formatCop ya fija el render a $0, pero un amountInCents
  // corrupto (NaN/Infinity/negativo) propagaba NaN por la aritmética (net NaN)
  // a cualquier superficie que consuma el struct (admin/Discord). Estructura
  // corrupta → ceros, nunca NaN. Money path intacto (webhook verifica contra
  // committed amount, nunca contra este cálculo informativo).
  // 2026-09-26 FASE 4 MEJORA planes-pagos (net honesto fail-closed,
  // research 2026 userflow "SaaS Pricing Models: Choose the Right Strategy" —
  // "a transparent pricing page lets a buyer estimate the bill without
  // contacting sales. Show the plan price, the billing unit, the limits, and
  // how charges change":
  // https://www.userflow.com/blog/saas-pricing-strategy-a-comprehensive-guide-to-choosing-the-right-model):
  // el fix 2026-09-24 cerró el NaN (variable→0) pero dejó el fijo $700 sobre
  // monto $0 y `net = 0 − fee` NEGATIVO — un neto "−$833" en admin/Discord es
  // una mentira contable (sin transacción no hay retención Wompi que absorber).
  // Sin monto válido → struct todo-ceros; con monto válido pero menor que el
  // fee (input degenerado, imposible vía getBillingPlan — el plan real mínimo
  // Go $4 ≈ $16k COP ≫ fee) el neto se fija a 0, nunca negativo. La identidad
  // happy-path net+fee=total queda intacta. Money path intacto (informativo).
  // FASE 4 MEJORA MASIVA v2 oferta-8 B1 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 vezadigital "transparent savings/costs, no hidden fees":
  // el desglose nunca rompe la superficie). Antes: `plan.amountInCents` sin
  // guard — un plan null runtime (getBillingPlan fallido propagado a
  // admin/Discord) lanzaba TypeError en vez de struct ceros (paridad FASE 4
  // D5 wompiTaxesFor, que sí era null-safe). Ahora: plan ausente → ceros.
  // Fingerprint `wompi-retention-null-safe-zero`.
  if (!plan || typeof plan !== "object") {
    return {
      feeVariableCop: 0,
      feeFixedCop: 0,
      feeIvaCop: 0,
      totalFeeCop: 0,
      netCop: 0,
    };
  }
  const amountCop =
    Number.isFinite(plan.amountInCents) && plan.amountInCents > 0
      ? plan.amountInCents / 100
      : 0;
  if (amountCop <= 0) {
    return {
      feeVariableCop: 0,
      feeFixedCop: 0,
      feeIvaCop: 0,
      totalFeeCop: 0,
      netCop: 0,
    };
  }
  const feeVariableCop = Math.round(amountCop * WOMPI_FEE_PERCENT);
  const feeFixedCop = WOMPI_FEE_FIXED_COP;
  const feeSubtotal = feeVariableCop + feeFixedCop;
  const feeIvaCop = Math.round(feeSubtotal * IVA_RATE);
  const totalFeeCop = feeSubtotal + feeIvaCop;
  const netCop = Math.max(0, amountCop - totalFeeCop);
  return { feeVariableCop, feeFixedCop, feeIvaCop, totalFeeCop, netCop };
}

/**
 * 2026-09-23 FASE 2 FIXES PAY-08 (research docs.wompi.co Impuestos 2026-09-23:
 * `taxes:[{type,amount_in_cents}]` en POST /transactions es informativo y NO
 * se suma al total — base+IVA=total ya cobrado en `amountInCents`). Deriva del
 * SSOT `ivaAmountCents` (sin literales): las rutas API que crean la
 * transacción server-side (subscription/transactions/process-renewals) lo
 * envían; el Web Checkout hosted ya lo manda como `tax-in-cents:vat` (ver
 * `wompi-web-checkout.ts:38-40`). Sin IVA (>0) → array vacío.
 */
export function wompiTaxesFor(plan: BillingPlan | null | undefined): Array<{
  type: "VAT";
  amount_in_cents: number;
}> {
  // FASE 4 oferta-8 D5 (wire): plan null runtime → [] (nunca throw).
  if (!plan || typeof plan !== "object") return [];
  if (!Number.isFinite(plan.ivaAmountCents) || plan.ivaAmountCents <= 0)
    return [];
  return [{ type: "VAT", amount_in_cents: plan.ivaAmountCents }];
}

/**
 * 2026-09-24 FASE 4G MEJORA planes-pagos [P2] rango de plan (research 2026:
 * vezadigital 2026-03-06 "Best SaaS Pricing Page Examples — Specify billing
 * cycle and unit, Transparent savings, Show annual discount" + fungies.io
 * 2026-05-27 "Annual billing toggle alone can increase…" + artisangrowth
 * 2026-04-10 "Framing annual discounts as two months free improves
 * conversions": Pricing/Changelog ya comparan planes por precio suelto —
 * go<pro<max<maxplus centraliza upgrade/downgrade/same para CTAs honestos
 * ("Current" vs checkout vs cambio). Desconocido → 0 fail-closed (nunca
 * concede rango). Puro, sin copy, sin visual, Lovable intacto.
 * https://www.vezadigital.com/post/best-saas-pricing-page-examples
 * https://fungies.io/saas-pricing-page-best-practices-2026/
 *
 * 2026-09-25 RUN1 FASE2 planRank SSOT (research 2026 vezadigital 2026-03-06,
 * SEED=2026-09-25-c6c873bf): eliminada la tabla duplicada 0-4 sin free — esta
 * función delega en la escala ÚNICA de `@/lib/api-utils` (free=1 < go=2 <
 * pro=3 < max=4 < maxplus=5, desconocido=0). Delegación y no re-export para
 * no romper los mocks parciales de `@/lib/billing-plans` en tests
 * (Pricing.test.tsx y route.test.ts mockean este módulo sin importOriginal:
 * un `export { planRank }` rebotaría a undefined). Dirección elegida porque
 * `api-utils` ya es la escala consumida por las 8 superficies money
 * (webhook/web-checkout/transactions/subscription/entitlement/Pricing/
 * CheckoutContent/use-pricing) — cero cambios en consumidores.
 */
export function planRank(plan: string | null | undefined): number {
  return apiPlanRank(plan);
}

/** True cuando `to` es un plan superior a `from` (upgrade con pago).
 * 2026-09-25 RUN1 FASE2: con la escala única free=1, free→go SÍ es upgrade
 * (antes false por el guard from>0 con free=0); desconocido→paid sigue false
 * (fail-closed, from=0). SEED=2026-09-25-c6c873bf. */
export function isPlanUpgrade(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return planRank(to) > planRank(from) && planRank(from) > 0;
}

/** True cuando `to` es un plan inferior a `from` (baja al fin del ciclo).
 * 2026-09-25 RUN1 FASE2: con la escala única free=1, go→free SÍ es downgrade
 * (antes false por el guard to>0 con free=0); paid→desconocido sigue false
 * (fail-closed, to=0). SEED=2026-09-25-c6c873bf. */
export function isPlanDowngrade(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return planRank(to) < planRank(from) && planRank(to) > 0;
}

/**
 * 2026-09-24 competencia-3 FASE 5 planes-pagos P1+P2 (research 2026 pricing
 * guide 2026-03-06 + 13 best practices "remember billing preference" +
 * pricing transparency "usage resets in H:MM:SS" — industry patterns; ver
 * websearch 2026 "Next.js proxy matcher CSP nonce best practice" para el
 * requisito de cita 2026 del fixer residual): helpers puros de preferencia
 * Max|Max+ + countdown a 00:00 UTC. SIN precios — SSOT intacto
 * (billing-plans solo expone constantes/funciones derivadas; estos helpers
 * no tocan montos ni tasas).
 */

/** Storage key de la preferencia Max|Max+ (localStorage, best-effort). */
export const MAX_TIER_PREFERENCE_KEY = "vortixy:preferred-max-tier";

/** Preferencia válida del toggle Max|Max+: solo "max" o "maxplus". */
export type PreferredMaxTier = "max" | "maxplus";

/** Normaliza cualquier valor a "maxplus" solo si es exacto, resto a "max". */
export function resolvePreferredMaxTier(value: unknown): PreferredMaxTier {
  // FASE 4 MEJORA MASIVA v2 oferta-8 B3 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 fungies.io "show annual upfront, let users switch": el toggle
  // Max|Max+ persiste en localStorage — valor editable por el usuario). Antes:
  // comparación estricta `=== "maxplus"` — "MAXPLUS" o " maxplus " (pegado con
  // espacios/mayúscula) caían a "max" en silencio aunque el usuario eligió
  // Max+. Ahora: trim+lowercase (paridad FASE 4 D3 / FASE 6 P2). Sanos intactos.
  // Fingerprint `preferred-max-tier-trim-lower-normalize`.
  return typeof value === "string" && value.trim().toLowerCase() === "maxplus"
    ? "maxplus"
    : "max";
}

interface PreferredMaxTierStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

/** Lee la preferencia persistida; storage ausente/roto → "max" (nunca throw). */
export function readPreferredMaxTier(
  storage: PreferredMaxTierStorage | null | undefined,
): PreferredMaxTier {
  try {
    if (!storage || typeof storage.getItem !== "function") return "max";
    return resolvePreferredMaxTier(storage.getItem(MAX_TIER_PREFERENCE_KEY));
  } catch {
    return "max";
  }
}

/** Persiste la preferencia normalizada; storage roto → no-op (nunca throw). */
export function persistPreferredMaxTier(
  storage: PreferredMaxTierStorage | null | undefined,
  tier: unknown,
): void {
  try {
    if (!storage || typeof storage.setItem !== "function") return;
    storage.setItem(MAX_TIER_PREFERENCE_KEY, resolvePreferredMaxTier(tier));
  } catch {
    // best-effort: localStorage denegado (privado) no rompe el pricing.
  }
}

/** Formatea ms restantes a "H:MM:SS" para el reset 00:00 UTC; inválido → "0:00:00". */
export function formatResetCountdown(value: unknown): string {
  const ms = typeof value === "number" ? value : NaN;
  if (!Number.isFinite(ms) || ms <= 0) {
    if (ms === 0) return "0:00:00";
    return "0:00:00";
  }
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * 2026-09-25 FASE 4 complemento C P1 aviso FX stale (research 2026 Nexway
 * "SaaS Checkout Localization" — "avoiding hidden FX conversions at checkout
 * stage" + AllRatesToday 2026 "Best Exchange Rate API for SaaS billing":
 * "Transparency: customers can verify your prices" —
 * https://www.nexway.com/blog/saas-checkout-localization-best-practices-2026
 * https://allratestoday.com/blog/best-exchange-rate-api-for-saas-billing-2026):
 * el proveedor base (open.er-api.com) actualiza a DIARIO, no tick-by-tick —
 * una tasa "live" con timestamp de hace >24h puede no reflejar el mercado.
 * Umbral SSOT de frescura para el aviso en checkout (display únicamente:
 * nunca toca montos — el cargo exacto se fija al pagar y el webhook verifica
 * contra el committed amount). Vive aquí y no en fx.ts porque fx.ts importa
 * node:async_hooks vía logger (no client-safe) y CheckoutContent es "use
 * client". null/inválida/futura (skew de reloj) → false: el pin/catálogo ya
 * tiene su propia nota y nunca se grita stale sobre un fallback.
 */
export const FX_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export function isFxRateStale(
  updatedAt: number | null | undefined,
  now: number = Date.now(),
): boolean {
  if (
    typeof updatedAt !== "number" ||
    !Number.isFinite(updatedAt) ||
    !Number.isFinite(now) ||
    updatedAt <= 0
  )
    return false;
  const age = now - updatedAt;
  if (!Number.isFinite(age) || age <= 0) return false;
  return age > FX_STALE_AFTER_MS;
}

/**
 * 2026-09-25 RUN1 FASE4 MEJORA planes-pagos M1 mapa pending acotado
 * (SEED=2026-09-25-c6c873bf; research 2026 Moesif "SaaS Billing Best
 * Practices 2026" — idempotency identifier por intento + Stigg 2026 top-40
 * upgrades/downgrades "one change at a time":
 * https://www.stigg.io/blog-posts/weve-tested-the-top-40-saas-companies-this-is-how-they-handle-upgrades-downgrades-part-1):
 * `pending_transactions[reference]` en app_metadata es schemaless y cada
 * checkout concurrente (doble clic, reintento, upgrade+compra) añadía una
 * entrada sin cota — app_metadata de Auth tiene techo de tamaño y un mapa
 * gigante rompe getUserById antes que el webhook. Tope SSOT 5 (ventana
 * 48h del reconcile: 5 intentos recientes bastan para verificar; el slot
 * global `pending_transaction_*` sigue siendo el fallback para compras
 * viejas). Puro, sin precios, sin copy, Lovable intacto.
 */
export const PENDING_TX_MAP_LIMIT = 5;

export interface PendingTxEntry {
  amount_cents: number;
  rate: number;
  plan: string;
  period: string;
  created_at: number;
}

/** Acota el mapa a las N entradas más recientes + la nueva (orden inserción). */
export function prunePendingTxMap(
  prev: Record<string, unknown> | null | undefined,
  reference: string,
  entry: PendingTxEntry,
): Record<string, PendingTxEntry> {
  const next: Record<string, PendingTxEntry> = {};
  if (prev && typeof prev === "object") {
    for (const [key, value] of Object.entries(prev)) {
      if (key === reference) continue;
      if (
        value !== null &&
        typeof value === "object" &&
        typeof (value as Record<string, unknown>).amount_cents === "number"
      ) {
        next[key] = value as PendingTxEntry;
      }
    }
  }
  next[reference] = entry;
  const keys = Object.keys(next);
  const overflow = keys.length - PENDING_TX_MAP_LIMIT;
  for (let i = 0; i < overflow; i++) {
    delete next[keys[i] as string];
  }
  return next;
}

/**
 * 2026-09-25 RUN1 FASE4 MEJORA planes-pagos M2 coalescencia anti-doble-cobro
 * (SEED=2026-09-25-c6c873bf; research 2026 Stigg top-40 — el cambio se
 * agenda/es serializa, nunca dos cargos paralelos por el mismo intent +
 * Moesif 2026 smart-retries con ventana por causa):
 * doble clic / retry del cliente generaba DOS referencias Wompi (randomUUID
 * por intento) y DOS cargos reales por el mismo plan. Si el usuario ya tiene
 * un pending del MISMO plan+periodo creado hace <5 min, la ruta responde 429
 * (clave `rateLimited` existente — cero keys i18n nuevas, PROHIBIDO tocar
 * `src/lib/i18n/*.ts`) con Retry-After en vez de crear el 2º cargo. Ventana
 * corta a propósito: pasado el coalescing el usuario puede reintentar (el
 * pending viejo lo sana el webhook/reconcile). Distinto plan/periodo o
 * pending viejo → false (no bloquea upgrades legítimos). Puro, sin precios.
 */
export const PENDING_CHECKOUT_COALESCE_MS = 5 * 60 * 1000;

function recentPendingEntryCreatedAt(
  value: unknown,
  plan: string,
  period: string,
  nowMs: number,
): number | null {
  if (value === null || typeof value !== "object") return null;
  const entry = value as Record<string, unknown>;
  // FASE 4 MEJORA MASIVA v2 oferta-8 B5 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 Stigg top-40 "one change at a time" + Moesif 2026
  // idempotency: el coalesce 5min→429 es el guard anti-doble-cobro). Antes:
  // `entry.plan !== plan` estricto — un pending guardado "pro"/"monthly" vs
  // intento "Pro"/"Monthly" (query con mayúscula) NO coalescía y creaba el 2º
  // cargo real. Ahora: comparación trim+lowercase en ambos lados (paridad
  // FASE 4 D3 / FASE 6 P2); a propósito SIN fallback a "monthly" (un periodo
  // basura no debe equivaler a monthly y bloquear compras legítimas). Sanos
  // intactos (canónicos ya minúsculos → identidad).
  // Fingerprint `billing-coalesce-plan-period-normalize`.
  // 2026-09-28 FASE 2 RUN6 PAY6-07: devuelve el `created_at` del pending que
  //|matcha| en vez de un boolean, para que el 429 pueda derivar su Retry-After
  // del MISMO predicado (una sola fuente de verdad, sin drift).
  const norm = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : v;
  if (norm(entry.plan) !== norm(plan) || norm(entry.period) !== norm(period))
    return null;
  const createdAt =
    typeof entry.created_at === "number"
      ? entry.created_at
      : typeof entry.pending_transaction_created_at === "number"
        ? entry.pending_transaction_created_at
        : Number.NaN;
  if (!Number.isFinite(createdAt) || createdAt <= 0) return null;
  const age = nowMs - createdAt;
  return Number.isFinite(age) && age >= 0 && age < PENDING_CHECKOUT_COALESCE_MS
    ? createdAt
    : null;
}

/** Itera los dos contenedores (slot global + mapa) y devuelve el `created_at`
 *  MÁS RECIENTE que coalesce, o null si no hay ninguno. */
function latestCoalescingPendingCreatedAt(
  appMetadata: Record<string, unknown> | null | undefined,
  plan: string,
  period: string,
  nowMs: number,
): number | null {
  if (!appMetadata || typeof appMetadata !== "object") return null;
  if (!Number.isFinite(nowMs)) return null;
  let latest: number | null = null;
  const track = (createdAt: number | null) => {
    if (createdAt !== null && (latest === null || createdAt > latest)) {
      latest = createdAt;
    }
  };
  // Slot global (compat compras viejas): pending_plan + created_at.
  if (
    appMetadata.pending_plan === plan &&
    appMetadata.pending_subscription_period === period
  ) {
    track(
      recentPendingEntryCreatedAt(
        {
          plan: appMetadata.pending_plan,
          period: appMetadata.pending_subscription_period,
          pending_transaction_created_at:
            appMetadata.pending_transaction_created_at,
        },
        plan,
        period,
        nowMs,
      ),
    );
  }
  // Mapa por-referencia (PAY-01): cubre el caso donde el slot global ya fue
  // sobrescrito por otro intento pero el pending mismo-plan sigue vivo.
  const txMap = appMetadata.pending_transactions;
  if (txMap !== null && typeof txMap === "object") {
    for (const value of Object.values(txMap as Record<string, unknown>)) {
      track(recentPendingEntryCreatedAt(value, plan, period, nowMs));
    }
  }
  return latest;
}

export function isRecentPendingCheckout(
  appMetadata: Record<string, unknown> | null | undefined,
  plan: string,
  period: string,
  nowMs: number = Date.now(),
): boolean {
  return (
    latestCoalescingPendingCreatedAt(appMetadata, plan, period, nowMs) !== null
  );
}

/**
 * 2026-09-28 FASE 2 RUN6 PAY6-07 (SEED=2026-09-28-eea43113, research 2026
 * RFC 6585 §4: el 429 "MAY include a Retry-After header indicating how long to
 * wait before making a new request" — o sea la espera REAL, no una adivinanza;
 * y draft-polli-ratelimit-headers-02 §6.3: "if a response contains both the
 * Retry-After and the RateLimit-Reset header fields, the value of RateLimit-Reset
 * SHOULD reference the same point in time as Retry-After" — por eso
 * `apiRateLimited` deriva ambos del mismo argumento):
 * segundos que faltan para que expire el pending que coalesce.
 *
 * Antes las 6 salidas anti-doble-cobro (web-checkout/transactions/
 * subscription) respondían `apiRateLimited(locale, 60)` con un guard real de
 * 5 min ⇒ el cliente obedecía `Retry-After: 60`, reintentaba a los 60s y
 * recibía 429 otras cuatro veces hasta el minuto 5.
 *
 * Piso 1 (nunca 0 ni negativo: `Retry-After: 0` es "reintentá ya" y reproduce
 * el loop). Sin pending legible → la ventana completa (fail-closed conservador:
 * esperar de más converge, esperar de menos miente).
 */
export function pendingCheckoutRetryAfterSeconds(
  appMetadata: Record<string, unknown> | null | undefined,
  plan: string,
  period: string,
  nowMs: number = Date.now(),
): number {
  const createdAt = latestCoalescingPendingCreatedAt(
    appMetadata,
    plan,
    period,
    nowMs,
  );
  if (createdAt === null) return PENDING_CHECKOUT_COALESCE_MS / 1000;
  const remainingMs = createdAt + PENDING_CHECKOUT_COALESCE_MS - nowMs;
  return Math.max(1, Math.ceil(remainingMs / 1000));
}

/**
 * Retry-After honest para la rama `busy` del lock anti-doble-cobro
 * (`acquireCheckoutLock`, ver `wompi-checkout-lock.ts`): su lease es de 5 min,
 * la misma ventana que el coalesce de arriba. Se deriva del SSOT de esa ventana
 * para no duplicar el literal; el lock se libera mucho antes en el caso normal
 * (tras el write del ganador), así que esperar de más converge y esperar de
 * menos mentiría. research 2026: misma cita RFC 6585 §4 que arriba.
 */
export const CHECKOUT_LOCK_BUSY_RETRY_AFTER_SECONDS =
  PENDING_CHECKOUT_COALESCE_MS / 1000;
